import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';

type AnalyticsInitOptions = {
  apiHost?: string;
};

function isProdEnabled(): boolean {
  return import.meta.env.PROD && !!import.meta.env.VITE_POSTHOG_KEY;
}

function sanitizeProperties(properties?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!properties) return undefined;
  // Avoid accidentally sending raw objects like Error, Response, etc.
  return JSON.parse(JSON.stringify(properties));
}

class Analytics {
  private initialized = false;

  init(apiKey: string, options?: AnalyticsInitOptions) {
    if (this.initialized) return;
    if (!import.meta.env.PROD) return;
    if (!apiKey) return;

    const apiHost = options?.apiHost ?? import.meta.env.VITE_POSTHOG_HOST ?? 'https://app.posthog.com';

    posthog.init(apiKey, {
      api_host: apiHost,
      autocapture: false,
      capture_pageview: false,
      disable_session_recording: true,
      persistence: 'localStorage',
    });

    this.initialized = true;
  }

  identify(userId: string, traits?: Record<string, unknown>) {
    if (!this.initialized) return;
    posthog.identify(userId, sanitizeProperties(traits));
  }

  track(eventName: string, properties?: Record<string, unknown>) {
    // GA4 (gtag) — fired independently of PostHog init: the tag is loaded globally
    // in index.html, so funnel events land in GA even without a PostHog key.
    try {
      const g = (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag;
      if (typeof g === 'function') g('event', eventName, sanitizeProperties(properties) ?? {});
    } catch {
      /* analytics must never throw into the app */
    }

    if (!this.initialized) return;

    const propagation = Sentry.getCurrentScope().getPropagationContext?.();

    const enrichedProperties: Record<string, unknown> = {
      ...(sanitizeProperties(properties) ?? {}),
      timestamp: new Date().toISOString(),
      environment: import.meta.env.MODE,
      sentry_trace_id: propagation?.traceId,
      sentry_span_id: propagation?.spanId,
    };

    posthog.capture(eventName, enrichedProperties);

    // Mirror into Sentry breadcrumbs for faster issue triage.
    Sentry.addBreadcrumb({
      category: 'analytics',
      message: eventName,
      data: sanitizeProperties(properties),
      level: 'info',
    });
  }

  page(pageName: string, properties?: Record<string, unknown>) {
    this.track('page_viewed', { page_name: pageName, ...(properties ?? {}) });
  }

  reset() {
    if (!this.initialized) return;
    posthog.reset();
  }
}

export const analytics = new Analytics();

export function initAnalytics(options?: AnalyticsInitOptions) {
  if (!isProdEnabled()) return;
  analytics.init(import.meta.env.VITE_POSTHOG_KEY as string, options);
}

export const track = (eventName: string, properties?: Record<string, unknown>) =>
  analytics.track(eventName, properties);
export const identify = (userId: string, traits?: Record<string, unknown>) =>
  analytics.identify(userId, traits);
export const page = (pageName: string, properties?: Record<string, unknown>) =>
  analytics.page(pageName, properties);
export const reset = () => analytics.reset();


